import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, X } from 'lucide-react';
import { 
  generateHeadline, 
  calculateOverallGrade, 
  getGradeColor,
  getStrengthStretchTags,
  generateAISummary,
  type ProficiencyData,
} from '@/utils/technicianPassportUtils';
import {
  TECHNICIAN_TYPE_OPTIONS,
  SOURCE_BADGE_CONFIG,
  type TechnicianTypeKey,
  type TechnicianTypeSource,
} from '@/utils/resolveTechnicianTypeTitle';
import type { DirectMetrics } from '@/hooks/useDirectTechnicianMetrics';

interface PassportSnapshotCardProps {
  profile: {
    full_name: string;
    profile_photo_url?: string | null;
    is_active: boolean;
    optibay_email?: string | null;
  };
  proficiencies: ProficiencyData[];
  metrics: DirectMetrics | null;
  /** Resolved technician type title (display string) */
  technicianTypeTitle?: string;
  /** Source of the title */
  technicianTypeSource?: TechnicianTypeSource;
  /** Whether the title is locked (non-editable) */
  technicianTypeIsLocked?: boolean;
  /** Callback when user changes self-reported title */
  onTechnicianTypeChange?: (typeKey: TechnicianTypeKey) => void;
  /**
   * The proficiency / metrics reads FAILED.
   *
   * This card is MIXED: the person's name, photo and title do not come from
   * those reads and remain true, but the grade, the strength/stretch tags and
   * the summary paragraph are all COMPUTED from them. Computing them from an
   * empty set produces a grade and a written characterisation of someone's
   * work out of a network error, which is a fact manufactured from a failure.
   * The identity half is kept; the derived half says it could not be loaded.
   */
  derivedInputsFailed?: boolean;
}

export function PassportSnapshotCard({ 
  profile, 
  proficiencies, 
  metrics,
  technicianTypeTitle = 'Technician',
  technicianTypeSource = 'none',
  technicianTypeIsLocked = true,
  onTechnicianTypeChange,
  derivedInputsFailed = false,
}: PassportSnapshotCardProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  
  // generateHeadline is kept only for the AI summary paragraph
  const headline = generateHeadline(proficiencies);
  const grade = calculateOverallGrade(metrics, proficiencies);
  const tags = getStrengthStretchTags(proficiencies);
  const summary = generateAISummary(metrics, proficiencies, headline);
  
  const initials = profile.full_name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const sourceBadge = SOURCE_BADGE_CONFIG[technicianTypeSource];
  const canEdit = !technicianTypeIsLocked && !!onTechnicianTypeChange;

  return (
    <Card className="overflow-hidden" data-testid="passport-snapshot">
      <div className="bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10 p-6">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Avatar + Status */}
          <div className="relative">
            <Avatar className="h-28 w-28 border-4 border-background shadow-lg">
              <AvatarImage src={profile.profile_photo_url || undefined} alt={profile.full_name} />
              <AvatarFallback className="text-2xl font-bold bg-primary text-primary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className={`absolute -bottom-1 -right-1 h-6 w-6 rounded-full border-2 border-background ${
              profile.is_active ? 'bg-success' : 'bg-muted'
            }`} />
          </div>

          {/* Name + Title + Tags */}
          <div className="flex-1 space-y-3">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">{profile.full_name}</h1>
                
                {/* Technician Type Title + Source Badge */}
                <div className="flex items-center gap-2 mt-1">
                  {isEditingTitle && canEdit ? (
                    <div className="flex items-center gap-2">
                      <Select
                        defaultValue=""
                        onValueChange={(val) => {
                          onTechnicianTypeChange!(val as TechnicianTypeKey);
                          setIsEditingTitle(false);
                        }}
                      >
                        <SelectTrigger className="w-[220px] h-8 text-sm">
                          <SelectValue placeholder="Select type..." />
                        </SelectTrigger>
                        <SelectContent>
                          {TECHNICIAN_TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setIsEditingTitle(false)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-lg text-muted-foreground font-medium">
                        {technicianTypeTitle}
                      </p>
                      {sourceBadge.label && (
                        <Badge variant="outline" className={`text-xs ${sourceBadge.className}`}>
                          {sourceBadge.label}
                        </Badge>
                      )}
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => setIsEditingTitle(true)}
                        >
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      )}
                    </>
                  )}
                </div>

                <Badge variant={profile.is_active ? 'default' : 'secondary'} className="mt-2">
                  {profile.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              {/* Overall Grade — withheld when its inputs could not be read,
                  rather than graded from an empty set. */}
              {derivedInputsFailed ? (
                <div className="flex items-center justify-center h-20 w-20 rounded-2xl border border-dashed border-destructive/40 text-center">
                  <span className="text-[10px] leading-tight text-muted-foreground px-1">
                    Grade unavailable
                  </span>
                </div>
              ) : (
                <div className={`flex items-center justify-center h-20 w-20 rounded-2xl ${getGradeColor(grade)} shadow-lg`}>
                  <span className="text-3xl font-black">{grade}</span>
                </div>
              )}
            </div>

            {/* Strength/Stretch Tags — an empty set here would read as "this
                person has no strengths", which is not what a failed read means. */}
            <div className="flex flex-wrap gap-2">
              {!derivedInputsFailed && tags.map((tag, i) => (
                <Badge 
                  key={i}
                  variant="outline"
                  className={`
                    ${tag.type === 'strength' ? 'bg-success/10 text-success border-success/30' : ''}
                    ${tag.type === 'good' ? 'bg-warning/10 text-warning border-warning/30' : ''}
                    ${tag.type === 'stretch' ? 'bg-destructive/10 text-destructive border-destructive/30' : ''}
                  `}
                >
                  {tag.type === 'strength' && '✓ '}
                  {tag.type === 'stretch' && '↑ '}
                  {tag.category}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI Summary — a written characterisation of someone's work. Generated
          from proficiencies + metrics, so a failed read must not produce one. */}
      <CardContent className="pt-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {derivedInputsFailed
            ? 'Performance summary could not be loaded.'
            : summary}
        </p>
      </CardContent>
    </Card>
  );
}
